import { createApi } from '@reduxjs/toolkit/query/react';
import { type USState } from '@strata/constants';
import { DateFormats, formatDate } from '@strata/helper-functions';
import type dayjs from 'dayjs';

import { SCOPE_ASSESSOR_API_URL } from 'api/APIBase';
import { handleRTKQueryError } from 'services/Error';
import { getBaseQuery } from 'store/api/apiBaseQuery';

export interface MemberDetails {
    memberId: string;
    firstName: string;
    lastName: string;
    gender: string;
    dateOfBirth: string;
    phone?: string;
    secondaryPhone?: string;
    email?: string;
    memberAddress1?: string;
    memberAddress2?: string;
    memberCity?: string;
    memberState?: USState;
    memberZip?: string;
    alphaPrefix?: string;
    enrollmentStatus?: string;
    enrollmentStart?: string;
    enrollmentEnd?: string;
    isFmhEnrollmentEnd?: boolean;
    pendingAt?: string;
    willBeUnmanagedOn?: string;
    lastUpdated?: string;
}

export interface BenefitCheckResponse {
    patient_first_name?: string;
    patient_last_name?: string;
    patient_address_line_1?: string;
    patient_address_line_2?: string;
    member_city?: string;
    member_state?: USState;
    member_zip?: string;
    member_age?: number;
    member_phone?: string;
    gender?: string;
    coverage_start_date?: string;
    coverage_end_date?: string;
    benefit_group?: string;
    is_eligible: boolean;
    secondary_coverage_flag?: boolean;
    account?: string;
    attributes?: { attribute_type: string; attribute_values: string[] }[];
}

export const scopeAssessorApi = createApi({
    baseQuery: getBaseQuery(SCOPE_ASSESSOR_API_URL),
    reducerPath: 'scopeAssessorApi',
    endpoints: (build) => ({
        getMemberDetails: build.query<
            MemberDetails | undefined,
            {
                payerId: string;
                segmentId: string;
                memberId: string;
                firstName: string;
                lastName: string;
                dateOfBirth: string;
            }
        >({
            query: ({ segmentId, memberId, firstName, lastName, dateOfBirth, payerId }) => ({
                url: `${payerId}/members/${memberId}`,
                params: {
                    segment_id: segmentId,
                    firstname: firstName,
                    lastname: lastName,
                    dateofbirth: dateOfBirth,
                    ignore_dates: true,
                },
            }),
            transformResponse: (
                response: {
                    member_id: string;
                    firstname: string;
                    lastname: string;
                    dateofbirth: string;
                    gender: string;
                    phone?: string;
                    secondary_phone?: string;
                    email?: string;
                    member_address1?: string;
                    member_address2?: string;
                    member_city?: string;
                    member_state?: USState;
                    member_zip?: string;
                    alpha_prefix?: string;
                    enrollment_status?: string;
                    enrollment_start?: string;
                    enrollment_end?: string;
                    is_fmh_enrollment_end?: boolean;
                    pending_at?: string;
                    will_be_unmanaged_on?: string;
                    last_updated?: string;
                } | null,
            ) =>
                response
                    ? {
                          memberId: response.member_id,
                          firstName: response.firstname,
                          lastName: response.lastname,
                          gender: response.gender,
                          dateOfBirth: response.dateofbirth,
                          phone: response.phone,
                          secondaryPhone: response.secondary_phone,
                          email: response.email,
                          memberAddress1: response.member_address1,
                          memberAddress2: response.member_address2,
                          memberCity: response.member_city,
                          memberState: response.member_state,
                          memberZip: response.member_zip,
                          alphaPrefix: response.alpha_prefix,
                          enrollmentStatus: response.enrollment_status,
                          enrollmentStart: response.enrollment_start,
                          enrollmentEnd: response.enrollment_end,
                          isFmhEnrollmentEnd: response.is_fmh_enrollment_end,
                          pendingAt: response.pending_at,
                          willBeUnmanagedOn: response.will_be_unmanaged_on,
                          lastUpdated: response.last_updated,
                      }
                    : undefined,
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving patient details.',
            ),
        }),
        getBenefitCheckConfig: build.query<number, { payerId: string; segmentId: string }>({
            query: ({ payerId, segmentId }) => ({
                url: `${payerId}/benefit_check_config/${segmentId}`,
                params: {
                    option_name: 'delayed_unmanagement_days',
                },
            }),
        }),
        getBenefitCheck: build.query<
            BenefitCheckResponse,
            {
                memberId: string;
                dateOfBirth: NonNullable<dayjs.ConfigType>;
                firstName: string;
                segmentId: string;
                payerId: string;
            }
        >({
            query: ({ payerId, segmentId, memberId, dateOfBirth, firstName }) => ({
                url: `${payerId}/benefits/benefit-check`,
                params: {
                    cardholder_id: memberId,
                    date_of_birth: formatDate(dateOfBirth, DateFormats.HYPHEN_DATE),
                    first_name: firstName,
                    segment_id: segmentId,
                },
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred checking member benefits.',
            ),
        }),
    }),
});

export const {
    useGetBenefitCheckConfigQuery,
    useGetMemberDetailsQuery,
    useLazyGetBenefitCheckQuery,
} = scopeAssessorApi;
